import {
  Box,
  Button,
  Flex,
  Grid,
  Group,
  PasswordInput,
  Stack,
  Title,
  useMatches,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { DeleteAccountCard } from "../TabBilling/components";

import { TextSerif } from "@/components/elements";
import { UpdatePasswordInput, useUpdatePersonalInfo } from "@/services/profile/update-password";
import { getRenderIcon } from "@/utils/getIcon";

interface IFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const UserProfileTabSecurity = () => {
  const { t } = useTranslation();
  const isMobile = useMatches({ base: true, sm: false });
  const [updatePasswordError, setUpdatePasswordError] = useState<string | null>(null);
  const [newPasswordTouched, setNewPasswordTouched] = useState(false);
  const [passwordCriteria, setPasswordCriteria] = useState({
    number: false,
    special: false,
    uppercase: false,
    lowercase: false,
    length: false,
  });

  const responsiveGrid = { base: 6, sm: 4 };
  const fontSize = { base: "sm", md: "sm" };

  const { mutate: updatePassword, isPending } = useUpdatePersonalInfo({
    mutationConfig: {
      onSuccess: () => {
        form.reset();
        setPasswordCriteria({
          number: false,
          special: false,
          uppercase: false,
          lowercase: false,
          length: false,
        });
        setNewPasswordTouched(false);
        setUpdatePasswordError(null);
      },
      onError: () => {
        const message = t("profile.inputFields.password_update_failed");
        setUpdatePasswordError(message);
      },
    },
  });

  const minLengthPsw = 8;
  const minCharactersErrorText = t("profile.validation.min_characters", { length: minLengthPsw });

  const form = useForm<IFormValues>({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validateInputOnChange: true,
    validateInputOnBlur: true,
    validate: {
      currentPassword: (value) => (value.length < minLengthPsw ? minCharactersErrorText : null),
      newPassword: (value) =>
        isPasswordValid(value) ? null : t("profile.validation.password_not_valid"),
      confirmPassword: (value, values) =>
        value !== values.newPassword ? t("profile.validation.passwords_mismatch") : null,
    },
  });

  const updateCriteria = (value: string) => {
    setPasswordCriteria({
      number: /\d/.test(value),
      special: /[^A-Za-z0-9 ]/.test(value),
      uppercase: /[A-Z]/.test(value),
      lowercase: /[a-z]/.test(value),
      length: value.length >= minLengthPsw,
    });
  };

  const isPasswordValid = (value: string) => {
    return (
      /\d/.test(value) &&
      /[^A-Za-z0-9 ]/.test(value) &&
      /[A-Z]/.test(value) &&
      /[a-z]/.test(value) &&
      value.length >= minLengthPsw
    );
  };

  const handleSubmit = (values: typeof form.values) => {
    const payload: UpdatePasswordInput = {
      old_password: values.currentPassword,
      new_password: values.newPassword,
    };
    updatePassword(payload);
  };

  const isFormValid = () => {
    const { currentPassword, newPassword, confirmPassword } = form.values;
    const hasErrors = Object.values(form.errors).some((error) => error !== null);
    const allFieldsFilled = currentPassword && newPassword && confirmPassword;
    const passwordMeetsCriteria = Object.values(passwordCriteria).every(Boolean);

    return allFieldsFilled && passwordMeetsCriteria && !hasErrors && !isPending;
  };

  const criteriaStyle = (met: boolean) => ({
    color: !newPasswordTouched
      ? "inherit"
      : met
        ? "var(--mantine-color-success-7)"
        : "var(--mantine-color-danger-7)",
    fontWeight: !newPasswordTouched || met ? "normal" : "bold",
  });

  const renderIcon = getRenderIcon(newPasswordTouched);

  return (
    <Stack gap="lg">
      <Box p="md">
        <Group justify="space-between" align="center" h="2.5em">
          <Title order={3} fw="normal" fz="1.25em">
            {t("profile.carburate_access_settings")}
          </Title>
        </Group>
        <Box mb="lg">
          <TextSerif>{t("profile.password_change_alert")}</TextSerif>
        </Box>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="xl">
            <Stack>
              <PasswordInput
                label={t("profile.inputFields.current_password")}
                placeholder={t("profile.validation.enter_current_password")}
                h={80}
                {...form.getInputProps("currentPassword")}
                error={form.errors.currentPassword || updatePasswordError}
                onChange={(e) => {
                  form.getInputProps("currentPassword").onChange(e);
                  setUpdatePasswordError(null); // hide error message on typing
                }}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
              />
              <PasswordInput
                label={t("profile.inputFields.new_password")}
                placeholder={t("profile.validation.enter_new_password")}
                h={80}
                {...form.getInputProps("newPassword")}
                error={form.errors.newPassword}
                onChange={(e) => {
                  setNewPasswordTouched(true);
                  form.getInputProps("newPassword").onChange(e);
                  updateCriteria(e.currentTarget.value);
                }}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
              />
              <Stack gap="xs">
                <TextSerif fz={fontSize}>{t("profile.password_must_contain")}</TextSerif>
                <Box maw={500}>
                  <Grid gutter="md">
                    <Grid.Col span={responsiveGrid}>
                      <Flex
                        gap="sm"
                        align="baseline"
                        style={criteriaStyle(passwordCriteria.number)}
                      >
                        {renderIcon(passwordCriteria.number)}
                        <TextSerif fz={fontSize}>{t("profile.validation.1number")}</TextSerif>
                      </Flex>
                    </Grid.Col>
                    <Grid.Col span={responsiveGrid}>
                      <Flex
                        gap="sm"
                        align="baseline"
                        style={criteriaStyle(passwordCriteria.special)}
                      >
                        {renderIcon(passwordCriteria.special)}
                        <TextSerif fz={fontSize}>{t("profile.validation.1specialchar")}</TextSerif>
                      </Flex>
                    </Grid.Col>
                    <Grid.Col span={responsiveGrid}>
                      <Flex
                        gap="sm"
                        align="baseline"
                        style={criteriaStyle(passwordCriteria.uppercase)}
                      >
                        {renderIcon(passwordCriteria.uppercase)}
                        <TextSerif fz={fontSize}>{t("profile.validation.1uppercase")}</TextSerif>
                      </Flex>
                    </Grid.Col>
                  </Grid>
                  <Grid gutter="md" justify="flex-start">
                    <Grid.Col span={responsiveGrid}>
                      <Flex
                        gap="sm"
                        align="baseline"
                        style={criteriaStyle(passwordCriteria.lowercase)}
                      >
                        {renderIcon(passwordCriteria.lowercase)}
                        <TextSerif>{t("profile.validation.1lowercase")}</TextSerif>
                      </Flex>
                    </Grid.Col>
                    <Grid.Col span={responsiveGrid}>
                      <Flex
                        gap="sm"
                        align="baseline"
                        style={criteriaStyle(passwordCriteria.length)}
                      >
                        {renderIcon(passwordCriteria.length)}
                        <TextSerif fz={fontSize}>
                          {t("profile.validation.n_characters", { length: minLengthPsw })}
                        </TextSerif>
                      </Flex>
                    </Grid.Col>
                  </Grid>
                </Box>
              </Stack>
              <PasswordInput
                label={t("profile.inputFields.confirm_new_password")}
                placeholder={t("profile.validation.enter_new_password")}
                {...form.getInputProps("confirmPassword")}
                error={form.errors.confirmPassword}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
              />
            </Stack>
            <Button
              type="submit"
              color="primary"
              disabled={!isFormValid()}
              w={isMobile ? "100%" : "14em"}
              aria-label={t("common.save")}
            >
              {t("common.save")}
            </Button>
          </Stack>
        </form>
      </Box>

      <Box mt="lg" p={0} maw={{ base: "100%", sm: 700 }}>
        <DeleteAccountCard />
      </Box>
    </Stack>
  );
};
